/**
 * Load All Products for Apps/Solutions Pages
 * Loads remaining products beyond Shopify's 250 limit into DOM for pagination
 */
class LoadAllProducts {
  constructor() {
    this.init();
  }

  async init() {
    // Only run on apps/solutions pages
    if (!window.collectionData || !['apps', 'solutions'].includes(window.collectionData.handle)) {
      return;
    }

    const { handle, totalProducts, loadedProducts } = window.collectionData;
    
    if (loadedProducts >= totalProducts) {
      return;
    }

    await this.loadRemainingProducts(handle, loadedProducts, totalProducts);
  }

  async loadRemainingProducts(handle, loadedProducts, totalProducts) {
    const grid = document.getElementById('product-grids');
    if (!grid) return;

    let page = 2; // Start from page 2 since page 1 is already loaded
    let currentLoaded = loadedProducts;

    while (currentLoaded < totalProducts) {
      try {
        // Use the products.json API to get actual product data
        const response = await fetch(`/collections/${handle}/products.json?page=${page}&limit=250`);
        if (!response.ok) break;

        const data = await response.json();
        const products = data.products || [];
        
        if (products.length === 0) break;

        // Create product cards from API data and append to grid
        products.forEach(product => {
          const productCard = this.createProductCardFromAPI(product);
          if (productCard) {
            grid.appendChild(productCard);
          }
        });

        currentLoaded += products.length;
        
        page++;
        
        // Break if we got less than 250 products (last page)
        if (products.length < 250) break;
        
      } catch (error) {
        // Silently handle errors to avoid console warnings
        break;
      }
    }

    // Trigger pagination recalculation
    document.dispatchEvent(new CustomEvent('allProductsLoadedInDOM', {
      detail: { totalProducts: currentLoaded }
    }));
  }

  createProductCardFromAPI(product) {
    try {
      const productCard = document.createElement('div');
      productCard.className = 'exchange-product-card';
      
      const imageUrl = product.images && product.images[0] ? product.images[0].src : '';
      const productUrl = `/products/${product.handle}`;
      
      // Extract metafield data for filters - ensure no undefined values
      const appType = this.extractMetafield(product, 'product_type_custom') || '';
      const category = this.extractListMetafield(product, 'multicat') || this.extractMetafield(product, 'categories') || '';
      const priceType = this.extractMetafield(product, 'price_types') || '';
      const saviyntProduct = this.extractListMetafield(product, 'savprod') || this.extractMetafield(product, 'saviynt_products') || '';
      const region = this.extractMetafield(product, 'region') || '';
      const industry = this.extractMetafield(product, 'industry') || '';
      
      productCard.innerHTML = `
        <div class="card card-product text-start" 
             data-product-grid
             data-product-id="${product.id}"
             data-developer="${product.vendor || ''}"
             data-apptype="${appType}"
             data-category="${category}"
             data-pricetype="${priceType}"
             data-saviyntproduct="${saviyntProduct}"
             data-region="${region}"
             data-partner="${product.vendor || ''}"
             data-industry="${industry}"
             data-solutiontype="${appType}">
          <div class="product-image text-center" style="border-bottom: 1px solid rgba(0, 0, 0, 0.2);">
            <a href="${productUrl}" class="d-block product-link position-relative">
              ${imageUrl ? 
                `<img src="${imageUrl}" alt="${product.title}" loading="lazy" width="120" height="120" style="object-fit: contain;">` :
                `<svg class="placeholder-svg" width="120" height="120" viewBox="0 0 120 120" fill="none">
                   <rect width="120" height="120" fill="#f8f9fa"/>
                   <text x="60" y="60" text-anchor="middle" fill="#6c757d" font-size="12">No Image</text>
                 </svg>`
              }
            </a>
          </div>
          <div class="card-body px-3 py-3" style="display: flex; flex-direction: column; height: 250px;">
            <!-- Product Title Section (Fixed: 2 lines) -->
            <div class="product-title-section" style="height: 40px; margin-bottom: 8px;">
              <h2 class="product-title h6 mb-0 font-family-base" style="line-height: 20px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; font-size: 16px; margin-bottom: 0 !important;">
                ${product.title}
              </h2>
            </div>
            
            <!-- Badge Section (Fixed height to maintain alignment) -->
            <div class="badge-section" style="height: 20px; margin-bottom: 8px;">
              <!-- Badge placeholder for consistency -->
            </div>

            <!-- Vendor/Developer (Fixed: 1 line) -->
            <div class="product-vendor-section" style="height: 20px; margin-bottom: 8px;">
              ${product.vendor ? `<p class="product-vendor small mb-0 text-muted" style="font-size: 12px; line-height: 20px; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;">By ${product.vendor}</p>` : ''}
            </div>
            
            <!-- Description (Fixed: 3 lines) -->
            <div class="product-description-section" style="height: 60px; margin-bottom: 12px; overflow: hidden;">
              ${product.body_html ? `<p class="product-description small text-muted mb-0" style="font-size: 13px; line-height: 20px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;">${this.truncateText(this.stripHtml(product.body_html), 150)}</p>` : ''}
            </div>
            
            <!-- View Details Button (Fixed at bottom) -->
            <div class="product-actions" style="margin-top: auto;">
              <a href="${productUrl}" class="btn-view-details">View Details</a>
            </div>
          </div>
        </div>
      `;
      
      return productCard;
    } catch (error) {
      return null;
    }
  }

  stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  extractListMetafield(product, fieldName) {
    if (product.metafields && product.metafields.custom && product.metafields.custom[fieldName]) {
      const value = product.metafields.custom[fieldName];
      if (Array.isArray(value)) return value.join(',');
      try { const parsed = JSON.parse(value); if (Array.isArray(parsed)) return parsed.join(','); } catch(e) {}
      return value || '';
    }
    return '';
  }

  extractMetafield(product, fieldName) {
    // Try to extract from metafields if available
    if (product.metafields && product.metafields.custom && product.metafields.custom[fieldName]) {
      const value = product.metafields.custom[fieldName];
      return value || '';
    }
    
    // Fallback to tags for metafield data
    if (product.tags && Array.isArray(product.tags)) {
      for (const tag of product.tags) {
        if (tag.toLowerCase().includes(fieldName.toLowerCase().replace('_', ' '))) {
          return tag || '';
        }
      }
    }
    
    return '';
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new LoadAllProducts();
});